import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { FluxoResponse } from '@triade/contracts';
import { useFluxo } from '@/api/plano';
import { ErroLista } from '@/components/cargos';
import { Badge } from '@/components/ui/badge';
import type { ApiError } from '@/lib/api';
import { formatarData, formatarDinheiro, formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { type GranularidadeFluxo, GRANULARIDADES_FLUXO, ROTULO_GRANULARIDADE } from './fluxo-busca';
import { BotaoRelatorios } from '@/relatorios';
import { UsaAlimenta } from './usa-alimenta';

export { lerBuscaFluxo } from './fluxo-busca';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Fluxo de caixa projetado (/plano/fluxo). Entradas, saidas e saldos vem prontos da API; o saldo inicial de cada
 * periodo e o saldo final do periodo anterior (sem conta aqui). Impostos e frete ficam fora (pendencia registrada).
 */
export function FluxoPage({ de, ate, granularidade }: { de: string; ate: string; granularidade: GranularidadeFluxo }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const mudar = (patch: { de?: string; ate?: string; granularidade?: GranularidadeFluxo }) =>
    void navigate({ to: '/plano/fluxo', search: { de, ate, granularidade, ...patch }, replace: true });
  const fluxo = useFluxo({ de, ate, granularidade });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Fluxo de caixa
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Fluxo de caixa</h1>
          <p className="text-small text-ink-secondary">Recebimentos dos pedidos pelas parcelas, compras de MP, rubricas e comissões por período</p>
          <UsaAlimenta
            usa={[
              { rotulo: 'Carteira', to: '/plano/carteira' },
              { rotulo: 'Compras de MP', to: '/plano/compras' },
              { rotulo: 'Custo fábrica', to: '/plano/custo-fabrica' },
              { rotulo: 'Plano de vendas', to: '/configuracoes/plano' },
            ]}
          />
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="fluxo-de" className="text-caption font-medium text-ink-secondary">De</label>
            <input id="fluxo-de" type="date" className={CAMPO} value={de} onChange={(e) => mudar({ de: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="fluxo-ate" className="text-caption font-medium text-ink-secondary">Até</label>
            <input id="fluxo-ate" type="date" className={CAMPO} value={ate} onChange={(e) => mudar({ ate: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="fluxo-granularidade" className="text-caption font-medium text-ink-secondary">Agrupar por</label>
            <select id="fluxo-granularidade" className={CAMPO} value={granularidade} onChange={(e) => mudar({ granularidade: e.target.value as GranularidadeFluxo })}>
              {GRANULARIDADES_FLUXO.map((g) => (
                <option key={g} value={g}>
                  {ROTULO_GRANULARIDADE[g]}
                </option>
              ))}
            </select>
          </div>
          <BotaoRelatorios tela="plano-fluxo" params={{ de, ate, granularidade }} />
        </div>
      </div>

      {empresaId === null ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver o fluxo.</p>
      ) : (
        <section aria-label="Fluxo de caixa por período" aria-busy={fluxo.isFetching} className="rounded-2xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5 text-small">
            <span className="text-ink-secondary">Saldo inicial do fluxo</span>
            <span className="font-semibold tabular-nums" data-testid="saldo-inicial-fluxo">{formatarMoeda(fluxo.data?.saldoInicial)}</span>
          </div>
          {corpo(fluxo)}
          <div className="flex flex-col gap-1 border-t border-border px-4 py-3 text-small text-ink-secondary">
            <span role="note" data-testid="primeiro-negativo">
              {fluxo.data
                ? fluxo.data.primeiroNegativo
                  ? `Primeiro período com saldo negativo: ${formatarData(fluxo.data.primeiroNegativo)}`
                  : 'Nenhum período com saldo negativo.'
                : ' '}
            </span>
            <span>Impostos e frete ficam fora deste fluxo (pendência registrada).</span>
          </div>
        </section>
      )}
    </div>
  );
}

function corpo(fluxo: UseQueryResult<FluxoResponse, ApiError>): ReactNode {
  if (fluxo.isError && !fluxo.data) {
    return (
      <div className="p-4">
        <ErroLista erro={fluxo.error} onTentar={() => void fluxo.refetch()} titulo="Não foi possível carregar o fluxo" />
      </div>
    );
  }
  if (!fluxo.data) return <p role="status" className="px-4 py-6 text-small text-ink-muted">Carregando fluxo…</p>;
  if (fluxo.data.periodos.length === 0) return <p role="status" className="px-4 py-6 text-small text-ink-muted">Nenhum período no intervalo.</p>;

  const periodos = fluxo.data.periodos;
  const saldoInicial = fluxo.data.saldoInicial;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-small" aria-label="Fluxo por período">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="px-4 py-2.5 font-medium">Período</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Saldo inicial</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Entradas</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Compras de MP</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Rubricas</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Comissões</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Saídas</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Saldo do período</th>
            <th scope="col" className="px-3 py-2.5 text-right font-medium">Saldo final</th>
          </tr>
        </thead>
        <tbody>
          {periodos.map((p, i) => (
            <tr key={p.inicio} data-testid="linha-periodo" className="border-t border-line-soft">
              <td className="px-4 py-3 tabular-nums">
                {formatarData(p.inicio)} a {formatarData(p.fim)}
              </td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(i === 0 ? saldoInicial : periodos[i - 1]!.saldoAcumulado)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.entradas)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.compras)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.rubricas)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.comissoes)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.saidas)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatarDinheiro(p.saldoPeriodo)}</td>
              <td className="px-3 py-3 text-right font-semibold tabular-nums">
                {formatarDinheiro(p.saldoAcumulado)}
                {p.alerta && (
                  <Badge variant="destructive" className="ml-2">
                    Negativo
                  </Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
